import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Clock, ExternalLink, Flag, MapPin, Route as RouteIcon, X, Zap } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { statusVisitaLabel, proximaAcaoLabel } from './VisitaModal';
import { googleMapsDirLink, GOOGLE_MAPS_MAX_STOPS } from '../../../src/lib/routeOptimizer';

interface ParadaDetalhe {
    id: string;
    posicao: number;
    status: string;
    checkin_em?: string | null;
    checkout_em?: string | null;
    visitado_em?: string | null;
    descricao_visita?: string | null;
    status_visita?: string | null;
    proxima_acao?: string | null;
    foto_url?: string | null;
    negociacoes?: { titulo?: string | null; cidade?: string | null; bairro?: string | null; segmento?: string | null; consumo_medio_kwh?: number | null; latitude?: number | null; longitude?: number | null } | null;
}

interface RotaDetalhe {
    id: string;
    data: string;
    status: string;
    origem: string;
    nome?: string | null;
    observacoes?: string | null;
    distancia_total_km?: number | null;
    duracao_min?: number | null;
    created_at: string;
    iniciada_em?: string | null;
    concluida_em?: string | null;
    rota_paradas: ParadaDetalhe[];
}

interface RotaDetalheModalProps {
    rotaId: string;
    onClose: () => void;
}

const statusRotaLabel = (s: string) => ({
    planejada: 'Planejada', agendada: 'Agendada', em_execucao: 'Em execução',
    concluida: 'Concluída', cancelada: 'Cancelada', reagendada: 'Reagendada'
}[s] || s);

const statusRotaBadge = (s: string) => {
    switch (s) {
        case 'concluida': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        case 'em_execucao': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        case 'cancelada': return 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
        case 'agendada': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
        case 'reagendada': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
        default: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
};

const statusParada: Record<string, { label: string; cls: string; dot: string }> = {
    visitado: { label: 'Visitado', cls: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
    em_visita: { label: 'Em visita', cls: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
    pulado: { label: 'Pulado', cls: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
    pendente: { label: 'Não visitado', cls: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-300 dark:bg-gray-600' },
};

const hora = (iso?: string | null) => iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

const fmtDuracao = (min?: number | null) => {
    if (!min) return '';
    const h = Math.floor(min / 60);
    return h > 0 ? `${h}h${String(Math.round(min % 60)).padStart(2, '0')}` : `${Math.round(min)} min`;
};

/** Detalhe de uma rota do histórico: o que aconteceu em cada parada (Rotas v2). */
const RotaDetalheModal: React.FC<RotaDetalheModalProps> = ({ rotaId, onClose }) => {
    const [rota, setRota] = useState<RotaDetalhe | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ativo = true;
        setLoading(true);
        supabase
            .from('rotas_comerciais')
            .select('id, data, status, origem, nome, observacoes, distancia_total_km, duracao_min, created_at, iniciada_em, concluida_em, destino_lat, destino_lng, destino_label, rota_paradas(id, posicao, status, checkin_em, checkout_em, visitado_em, descricao_visita, status_visita, proxima_acao, foto_url, negociacoes(titulo, cidade, bairro, segmento, consumo_medio_kwh, latitude, longitude))')
            .eq('id', rotaId)
            .single()
            .then(({ data, error }) => {
                if (!ativo) return;
                if (error) { console.error(error); setRota(null); }
                else {
                    const r = data as unknown as RotaDetalhe;
                    r.rota_paradas?.sort((a, b) => a.posicao - b.posicao);
                    setRota(r);
                }
                setLoading(false);
            });
        return () => { ativo = false; };
    }, [rotaId]);

    const visitadas = rota?.rota_paradas.filter(p => p.status === 'visitado').length || 0;
    const total = rota?.rota_paradas.length || 0;
    const kwhTotal = rota?.rota_paradas.reduce((s, p) => s + Number(p.negociacoes?.consumo_medio_kwh || 0), 0) || 0;
    const paradasComGeo = rota
        ? rota.rota_paradas
            .filter(p => p.negociacoes?.latitude && p.negociacoes?.longitude)
            .map(p => ({ lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude! }))
        : [];
    // Ponto de chegada da rota (Prospecção). Sem ele o Maps terminaria na última empresa.
    const destinoRota = (rota as any)?.destino_lat != null && (rota as any)?.destino_lng != null
        ? { lat: Number((rota as any).destino_lat), lng: Number((rota as any).destino_lng) }
        : null;
    const mapsLink = googleMapsDirLink(paradasComGeo, undefined, destinoRota);
    const tetoMaps = destinoRota ? GOOGLE_MAPS_MAX_STOPS - 1 : GOOGLE_MAPS_MAX_STOPS;
    const mapsTruncado = paradasComGeo.length > tetoMaps;

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                            <RouteIcon size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                {rota?.nome || (rota ? `Rota de ${new Date(rota.data + 'T12:00:00').toLocaleDateString('pt-BR')}` : 'Detalhe da Rota')}
                            </h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                {rota ? (rota.origem === 'roteiro_diario' ? 'Roteiro Diário' : rota.origem === 'inteligente' ? 'Rota Inteligente' : rota.origem === 'mapa' ? 'Prospecção' : 'Roteirização') : 'Carregando...'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                {loading ? (
                    <div className="py-16"><LoadingSpinner label="Carregando detalhes..." size="sm" /></div>
                ) : !rota ? (
                    <div className="py-16 text-center text-sm text-gray-400">Não foi possível carregar a rota.</div>
                ) : (
                    <>
                        {/* Resumo */}
                        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusRotaBadge(rota.status)}`}>{statusRotaLabel(rota.status)}</span>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                {new Date(rota.data + 'T12:00:00').toLocaleDateString('pt-BR')} · {visitadas}/{total} visitas · ~{Math.round(rota.distancia_total_km || 0)} km
                                {rota.duracao_min ? ` · 🚦 ~${fmtDuracao(rota.duracao_min)}` : ''}
                            </span>
                            {kwhTotal > 0 && (
                                <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
                                    <Zap size={11} /> {Math.round(kwhTotal).toLocaleString('pt-BR')} kWh
                                </span>
                            )}
                        </div>

                        {(rota.iniciada_em || rota.concluida_em || rota.observacoes) && (
                            <div className="px-5 pt-3 space-y-1">
                                {rota.observacoes && <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">"{rota.observacoes}"</p>}
                                {rota.iniciada_em && (
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                        <Flag size={11} className="text-emerald-500" /> Iniciada às {hora(rota.iniciada_em)}
                                        {rota.concluida_em && <> · concluída às {hora(rota.concluida_em)}</>}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Paradas */}
                        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
                            {rota.rota_paradas.map(p => {
                                const sp = statusParada[p.status] || statusParada.pendente;
                                const permanencia = p.checkin_em && p.checkout_em
                                    ? Math.max(1, Math.round((new Date(p.checkout_em).getTime() - new Date(p.checkin_em).getTime()) / 60000))
                                    : null;
                                return (
                                    <div key={p.id} className="flex items-start gap-2.5 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                        <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 mt-0.5 ${p.status === 'visitado' ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            {p.status === 'visitado' ? '✓' : p.posicao}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{p.negociacoes?.titulo || `Parada ${p.posicao}`}</p>
                                                <span className={`text-[10px] font-bold flex items-center gap-1 shrink-0 ${sp.cls}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${sp.dot}`}></span>{sp.label}
                                                </span>
                                            </div>
                                            {(p.negociacoes?.bairro || p.negociacoes?.cidade) && (
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-0.5">
                                                    <MapPin size={10} /> {[p.negociacoes?.bairro, p.negociacoes?.cidade].filter(Boolean).join(', ')}
                                                </p>
                                            )}
                                            {/* Desfecho da visita */}
                                            {(p.status_visita || p.descricao_visita || p.checkin_em) && (
                                                <div className="mt-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-600 p-2 space-y-1">
                                                    {p.status_visita && (
                                                        <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">📋 {statusVisitaLabel(p.status_visita)}</p>
                                                    )}
                                                    {(p.checkin_em || p.checkout_em) && (
                                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                            <Clock size={10} />
                                                            {hora(p.checkin_em) || '—'} → {hora(p.checkout_em) || '—'}
                                                            {permanencia ? ` · ${permanencia} min no local` : ''}
                                                        </p>
                                                    )}
                                                    {p.descricao_visita && <p className="text-[11px] text-gray-600 dark:text-gray-300">{p.descricao_visita}</p>}
                                                    {p.proxima_acao && <p className="text-[10px] text-gray-500 dark:text-gray-400">→ Próxima ação: {proximaAcaoLabel(p.proxima_acao)}</p>}
                                                    {p.foto_url && (
                                                        <a href={p.foto_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline">
                                                            <ExternalLink size={10} /> Ver foto do medidor
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {total === 0 && <p className="text-center text-sm text-gray-400 py-6">Esta rota não tem paradas.</p>}
                        </div>

                        {mapsLink && (
                            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700">
                                <a
                                    href={mapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <ExternalLink size={14} />
                                    {mapsTruncado
                                        ? `Abrir trajeto no Google Maps (primeiras ${GOOGLE_MAPS_MAX_STOPS} de ${paradasComGeo.length})`
                                        : 'Abrir trajeto no Google Maps'}
                                </a>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

export default RotaDetalheModal;
